import { create } from 'zustand'

export type ToastKind = 'success' | 'error' | 'info' | 'loading'

export interface Toast {
  id: string
  kind: ToastKind
  message: string
  durationMs?: number // undefined で永続
}

interface ToastState {
  toasts: Toast[]
  show: (toast: Omit<Toast, 'id'>) => string
  update: (id: string, toast: Partial<Omit<Toast, 'id'>>) => void
  dismiss: (id: string) => void
  // 便利ショートカット
  success: (message: string, durationMs?: number) => string
  error: (message: string, durationMs?: number) => string
  info: (message: string, durationMs?: number) => string
  loading: (message: string) => string
}

const DEFAULT_DURATION: Record<ToastKind, number | undefined> = {
  success: 2000,
  error: 5000,
  info: 3000,
  loading: undefined,
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  show: ({ kind, message, durationMs }) => {
    const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
    const duration = durationMs ?? DEFAULT_DURATION[kind]
    set((s) => ({ toasts: [...s.toasts, { id, kind, message, durationMs: duration }] }))
    if (duration !== undefined) {
      setTimeout(() => get().dismiss(id), duration)
    }
    return id
  },

  update: (id, toast) => {
    set((s) => ({
      toasts: s.toasts.map((t) => (t.id === id ? { ...t, ...toast } : t)),
    }))
    // 更新時に新たなタイマーを設定（loading → success 等）
    if (toast.kind && toast.kind !== 'loading') {
      const duration = toast.durationMs ?? DEFAULT_DURATION[toast.kind]
      if (duration !== undefined) {
        setTimeout(() => get().dismiss(id), duration)
      }
    }
  },

  dismiss: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
  },

  success: (message, durationMs) => get().show({ kind: 'success', message, durationMs }),
  error: (message, durationMs) => get().show({ kind: 'error', message, durationMs }),
  info: (message, durationMs) => get().show({ kind: 'info', message, durationMs }),
  loading: (message) => get().show({ kind: 'loading', message }),
}))
